// Contact.tsx - Enhanced contact page with bourbon/wood theme, form validation,
// and EmailJS integration for sending messages

import React, { useRef, useState } from 'react';
import emailjs from 'emailjs-com';
import '../styles/Contact.css';

interface FormData {
  user_name: string;
  user_email: string;
  subject: string;
  message: string;
}

const Contact: React.FC = () => {
  // Reference to the form element
  const form = useRef<HTMLFormElement>(null);

  // Form state
  const [formData, setFormData] = useState<FormData>({
    user_name: '',
    user_email: '',
    subject: '',
    message: '',
  });

  // Form status
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitStatus, setSubmitStatus] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  // Handle input changes
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handler for form submission with dual email sending
  const sendEmail = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    if (form.current) {
      try {
        // EmailJS configuration
        const serviceId = 'service_l09crg6';
        const adminTemplateId = 'template_fj6k6kc'; // Admin notification template
        const autoReplyTemplateId = 'template_gvic9xe'; // Customer auto-reply template - REPLACE WITH NEW TEMPLATE ID
        const publicKey = 'b6PfNSSHrsCGf_bdq';

        // TEMPORARY: Disable auto-reply until new template is created
        const enableAutoReply = true; // Set to true after creating new template

        // Add a hidden input for timestamp if it doesn't exist
        const timeInput = form.current.querySelector(
          'input[name="time"]'
        ) as HTMLInputElement;
        if (!timeInput) {
          const hiddenTimeInput = document.createElement('input');
          hiddenTimeInput.type = 'hidden';
          hiddenTimeInput.name = 'time';
          hiddenTimeInput.value = new Date().toLocaleString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZoneName: 'short',
          });
          form.current.appendChild(hiddenTimeInput);
        } else {
          timeInput.value = new Date().toLocaleString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZoneName: 'short',
          });
        }

        // Send admin notification email using sendForm (more reliable)
        const adminResult = await emailjs.sendForm(
          serviceId,
          adminTemplateId,
          form.current,
          publicKey
        );

        console.log('Admin email sent successfully:', adminResult.text);

        // Send auto-reply confirmation to the customer's email address
        if (enableAutoReply) {
          try {
            console.log('=== AUTO-REPLY DEBUG INFO ===');
            console.log('Service ID:', serviceId);
            console.log('Template ID:', autoReplyTemplateId);
            console.log('Customer email:', formData.user_email);
            console.log('Public key:', publicKey);

            const autoReplyParams = {
              to_email: formData.user_email, // Customer's email address (where auto-reply goes)
              user_name: formData.user_name, // Customer's name
              user_email: formData.user_email, // Customer's email (for display in template)
              subject: formData.subject, // Customer's subject
              message: formData.message, // Customer's message
              time: new Date().toLocaleString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                timeZoneName: 'short',
              }),
            };

            console.log('Auto-reply parameters:', autoReplyParams);

            const autoReplyResult = await emailjs.send(
              serviceId,
              autoReplyTemplateId,
              autoReplyParams,
              publicKey
            );

            console.log(
              'Auto-reply sent successfully to:',
              formData.user_email,
              autoReplyResult.text
            );

            // Success - both emails sent
            setIsSubmitting(false);
            setSubmitStatus({
              success: true,
              message:
                "Message sent successfully! We'll get back to you shortly. Please check your email for a confirmation.",
            });
          } catch (autoReplyError: any) {
            console.error('=== AUTO-REPLY ERROR DEBUG ===');
            console.error('Error status:', autoReplyError.status);
            console.error('Error text:', autoReplyError.text);
            console.error('Full error:', autoReplyError);
            console.log('Template ID used:', autoReplyTemplateId);
            console.log('Customer email was:', formData.user_email);

            // If auto-reply fails, still show success since admin email worked
            setIsSubmitting(false);
            setSubmitStatus({
              success: true,
              message:
                "Message sent successfully! We've received your inquiry and will respond within 3 business days. (Note: Confirmation email may not have been delivered, but your message reached us!)",
            });
          }
        } else {
          // Auto-reply disabled - show success message for admin email only
          console.log('Auto-reply disabled - only admin email sent');
          setIsSubmitting(false);
          setSubmitStatus({
            success: true,
            message:
              "Message sent successfully! We've received your inquiry and will respond within 3 business days.",
          });
        }

        // Reset form
        form.current!.reset();
        setFormData({
          user_name: '',
          user_email: '',
          subject: '',
          message: '',
        });
      } catch (error: any) {
        console.error('Email sending failed:', error);
        setIsSubmitting(false);

        // Try to send just admin email if auto-reply fails
        if (error.text && error.text.includes('recipients address is empty')) {
          try {
            // If auto-reply failed, at least ensure admin email was sent
            console.log(
              'Auto-reply failed, but admin email should have been sent'
            );
            setSubmitStatus({
              success: true,
              message:
                'Your message was sent successfully! We received your inquiry and will respond within 3 business days. The confirmation email may not have been delivered, but your message reached us.',
            });

            // Reset form since admin email succeeded
            form.current!.reset();
            setFormData({
              user_name: '',
              user_email: '',
              subject: '',
              message: '',
            });
            return;
          } catch (retryError) {
            console.error('Retry failed:', retryError);
          }
        }

        // More specific error handling
        let errorMessage =
          'Failed to send message. Please try again later or contact us directly at sipnsec@gmail.com.';

        if (error.text && error.text.includes('recipients address is empty')) {
          errorMessage =
            "Your message was received! However, there was an issue sending the confirmation email. We'll still respond to your inquiry within 3 business days.";
        } else if (error.status === 422) {
          errorMessage =
            'There was a validation error. Please check your email address and try again.';
        } else if (error.status === 403) {
          errorMessage =
            'Service temporarily unavailable. Please try again in a few minutes or contact us directly.';
        }

        setSubmitStatus({
          success: false,
          message: errorMessage,
        });
      }
    }
  };

  return (
    <div className="contact-container">
      <div className="contact-header">
        <h1 className="section-title">Get In Touch</h1>
        <p className="contact-subtitle">
          Have questions about our merchandise, podcast, or just want to say
          hello? Reach out to Justin and Dave and the Sip'N'Security team!
        </p>
      </div>

      <div className="contact-content">
        <div className="contact-info">
          <div className="info-card">
            <div className="info-icon">📍</div>
            <h3>Find Us</h3>
            <p>8408 S. Quail Ct.</p>
            <p>Littleton, CO 80127</p>
          </div>

          <div className="info-card">
            <div className="info-icon">✉️</div>
            <h3>Email Us</h3>
            <p>sipnsec@gmail.com</p>
          </div>

          <div className="info-card">
            <div className="info-icon">📱</div>
            <h3>Call Us</h3>
            <p>720.833.1617</p>
          </div>

          <div className="info-card">
            <div className="info-icon">🌐</div>
            <h3>Connect With Us</h3>
            <div className="social-links">
              <a
                href="https://twitter.com/sipnsecurity"
                target="_blank"
                rel="noopener noreferrer"
                className="social-link"
              >
                Twitter
              </a>
              <a
                href="https://instagram.com/sipnsecurity"
                target="_blank"
                rel="noopener noreferrer"
                className="social-link"
              >
                Instagram
              </a>
              <a
                href="https://linkedin.com/in/sipnsecurity"
                target="_blank"
                rel="noopener noreferrer"
                className="social-link"
              >
                LinkedIn
              </a>
            </div>
          </div>
        </div>

        <div className="contact-form-container">
          <h2>Send Us A Message</h2>
          {submitStatus && (
            <div
              className={`status-message ${
                submitStatus.success ? 'success' : 'error'
              }`}
            >
              {submitStatus.message}
            </div>
          )}
          <form ref={form} onSubmit={sendEmail} className="contact-form">
            <label htmlFor="user_name">
              Full Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="user_name"
              name="user_name"
              value={formData.user_name}
              onChange={handleChange}
              required
            />
            <label htmlFor="user_email">
              Email Address <span className="required">*</span>
            </label>

            <input
              type="email"
              id="user_email"
              name="user_email"
              value={formData.user_email}
              onChange={handleChange}
              required
            />

            <label htmlFor="subject">
              Subject <span className="required">*</span>
            </label>
            <div className="form-group">
              <select
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
              >
                <option value="">Please select a subject</option>
                <option value="podcast">Podcast Question</option>
                <option value="merchandise">Merchandise Inquiry</option>
                <option value="collaboration">Collaboration Opportunity</option>
                <option value="feedback">Feedback</option>
                <option value="other">Other</option>
              </select>
            </div>

            <label htmlFor="message">
              Message <span className="required">*</span>
            </label>
            <div className="form-group">
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
              />
            </div>

            <button
              type="submit"
              className={`submit-button ${isSubmitting ? 'submitting' : ''}`}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>
      </div>

      <div className="contact-map">
        <div className="map-container">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d96746.24765734153!2d-105.08465264999999!3d39.613319849999995!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x876c5fe2e6306833%3A0x5149c5d60f65d40e!2sLittleton%2C%20CO!5e0!3m2!1sen!2sus!4v1666712345678!5m2!1sen!2sus"
            width="600"
            height="450"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Sip'N'Security Location"
          ></iframe>
        </div>
        <div className="visit-us">
          <h3>Visit Our Studio</h3>
          <p>
            Located in beautiful Littleton, Colorado, just south of Denver.
            Reach out to arrange a visit or to pick up merchandise in person!
          </p>
          <div className="business-hours">
            <p>
              <strong>Business Hours:</strong>
            </p>
            <p>Monday-Friday: 9am-5pm</p>
            <p>Saturday: By appointment</p>
            <p>Sunday: Closed</p>
          </div>
        </div>
      </div>

      <div className="faq-section">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-grid">
          <div className="faq-item">
            <h4>How can I be a guest on the podcast?</h4>
            <p>
              We're always looking for security experts and bourbon enthusiasts
              to join us! Select "Collaboration Opportunity" in the form above
              and tell us about yourself.
            </p>
          </div>
          <div className="faq-item">
            <h4>Do you ship merchandise internationally?</h4>
            <p>
              Yes! We ship our premium merchandise worldwide. Please allow 2-3
              weeks for international shipping.
            </p>
          </div>
          <div className="faq-item">
            <h4>Can I get a custom order?</h4>
            <p>
              For custom merchandise or bulk orders, please email
              sipnsec@gmail.com directly with your request.
            </p>
          </div>
          <div className="faq-item">
            <h4>How do I become a sponsor?</h4>
            <p>
              If you're interested in sponsoring the podcast, please select
              "Collaboration Opportunity" in the form above and we'll connect
              you with our partnership team.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
